import { AppState, Auth0ContextInterface } from "@auth0/auth0-react"

type GetAccessTokenSilentlyType = Pick<Auth0ContextInterface, "getAccessTokenSilently">["getAccessTokenSilently"]
type LoginWithRedirectType = Pick<Auth0ContextInterface, "loginWithRedirect">["loginWithRedirect"]

const RECOVERABLE_AUTH0_ERROR_CODES = new Set([
  "login_required",
  "consent_required",
  "missing_refresh_token",
  "invalid_grant",
  "invalid_refresh_token"
])

let accessTokenRequestInFlight: Promise<string> | null = null
let loginRedirectInFlight: Promise<void> | null = null

const getErrorField = (error: unknown, key: "error" | "error_description" | "message"): string => {
  if (!error || typeof error !== "object" || !(key in error)) {
    return ""
  }

  const value = (error as Record<string, unknown>)[key]
  return typeof value === "string" ? value : ""
}

const getDefaultAppState = (): AppState => {
  if (typeof window === "undefined") {
    return {}
  }

  return {
    returnTo: `${window.location.pathname}${window.location.search}`
  }
}

export const isRecoverableAuth0TokenError = (error: unknown): boolean => {
  const code = getErrorField(error, "error").toLowerCase()
  const description = `${getErrorField(error, "error_description")} ${getErrorField(error, "message")}`.toLowerCase()

  if (RECOVERABLE_AUTH0_ERROR_CODES.has(code)) {
    return true
  }

  return (
    description.includes("invalid refresh token") ||
    description.includes("missing refresh token") ||
    description.includes("login required")
  )
}

export const getAccessTokenSilentlyWithLock = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType
): Promise<string> => {
  if (!accessTokenRequestInFlight) {
    accessTokenRequestInFlight = getAccessTokenSilently().finally(() => {
      accessTokenRequestInFlight = null
    })
  }

  return accessTokenRequestInFlight
}

export const handleAuth0RecoverableError = async (
  error: unknown,
  loginWithRedirect: LoginWithRedirectType
): Promise<boolean> => {
  if (!isRecoverableAuth0TokenError(error)) {
    return false
  }

  if (!loginRedirectInFlight) {
    loginRedirectInFlight = loginWithRedirect({ appState: getDefaultAppState() })
      .catch(loginError => {
        console.error("Auth0 login redirect failed", loginError)
      })
      .finally(() => {
        loginRedirectInFlight = null
      })
  }

  await loginRedirectInFlight
  return true
}
